import { decryptWithDEK, encryptWithDEK } from "@/features/encryption/utils/encryption";

// Types for encrypted folder data
export type EncryptedFolderName =
  | {
      encrypted_data: string;
      iv: string;
    }
  | {
      encrypted_data: null;
      iv: null;
    };

/**
 * Encrypt folder name before sending to server
 * Only encrypts if encryption is enabled
 */
export async function encryptFolderName(name: string | null | undefined, dek: CryptoKey): Promise<EncryptedFolderName> {
  try {
    const nameToEncrypt = name || "";
    const { iv, ciphertext } = await encryptWithDEK(dek, nameToEncrypt);
    return {
      encrypted_data: ciphertext,
      iv,
    };
  } catch (error) {
    console.error("Failed to encrypt folder name:", error);
    return { encrypted_data: null, iv: null };
  }
}

/**
 * Decrypt folder name received from server
 * Only decrypts if encryption is enabled and data is available
 */
export async function decryptFolderName(encryptedData: string, iv: string, dek: CryptoKey): Promise<string> {
  try {
    return await decryptWithDEK(dek, encryptedData, iv);
  } catch (error) {
    console.error("Failed to decrypt folder name:", error);
    return "";
  }
}

/**
 * Process folder name by decrypting if encryption is enabled
 * Returns decrypted name or original name if no encryption
 */
export async function processFolderName(
  folder: {
    name: string | null;
    encrypted_data: string | null;
    iv: string | null;
  },
  dek: CryptoKey | null | undefined
): Promise<string> {
  if (dek && folder.encrypted_data && folder.iv) {
    return decryptFolderName(folder.encrypted_data, folder.iv, dek);
  }
  return folder.name || "";
}

/**
 * Prepare encrypted name for database operations
 * Returns encrypted data if available, otherwise null values
 */
export async function prepareEncryptedFolderName(
  name: string | null | undefined,
  dek: CryptoKey | null | undefined
): Promise<{
  encrypted_data: string | null;
  iv: string | null;
  clearName: boolean;
}> {
  if (!dek) {
    return { encrypted_data: null, iv: null, clearName: false };
  }

  const result = await encryptFolderName(name, dek);
  return {
    ...result,
    clearName: result.encrypted_data !== null && result.iv !== null,
  };
}
